// MARK://dhk8/pos/lib/clients/reports
// Typed client for management reports — F6.2 R3e / ADR-0008. Reads go through the graph
// (erp-reports subgraph, tenant del JWT vía RLS — NUNCA tenantId como argumento). Los
// read-models report_* los alimenta la SAGA del evento dhk8.pos.order.closed. Sin
// fallback de agregación client-side: la fuente de verdad es la proyección del grafo.
// Bajo PREVIEW_STUB se devuelven semillas para la demo sin backend.

import { gqlClient } from '../gql.js'
import {
  PosReportSalesQuery,
  PosReportTopProductsQuery,
  PosReportTipsQuery,
  PosReportTopServersQuery,
  PosReportCutsQuery,
} from '../queries/reports.queries.js'

// ── Report catalog (drives ReportsPage list) ───────────────────────────────

export type ReportId = 'ventas-por-dia' | 'productos-mas-vendidos' | 'propinas-por-empleado'

export interface ReportMeta {
  id: ReportId
  name: string
  description: string
}

export const REPORT_CATALOG: readonly ReportMeta[] = [
  {
    id: 'ventas-por-dia',
    name: 'Ventas por día',
    description: 'Total vendido, número de órdenes y ticket promedio por jornada.',
  },
  {
    id: 'productos-mas-vendidos',
    name: 'Productos más vendidos',
    description: 'Ranking de productos por unidades e importe en el periodo.',
  },
  {
    id: 'propinas-por-empleado',
    name: 'Propinas por empleado',
    description: 'Reparto de propinas (pool mas directas) por mesero en el periodo.',
  },
] as const

// ── DTOs ───────────────────────────────────────────────────────────────────

export interface DailySalesPoint {
  date: string // YYYY-MM-DD
  ordersCount: number
  salesCents: number
  avgTicketCents: number
}

export interface TopProductRow {
  productId: string
  name: string
  quantity: number
  totalCents: number
}

export interface TipsByEmployeeRow {
  employeeId: string
  employeeName: string
  pooledCents: number
  directCents: number
  totalCents: number
}

export interface CashCutReportRow {
  cashCutId: string
  branchId: string
  businessDate: string
  shiftId: string | null
  kind: string
  expectedCents: number
  countedCents: number
  varianceCents: number
  cutAt: string
}

export interface ReportRange {
  from: string // ISO
  to: string // ISO
  branchId?: string
}

// ── Helpers ────────────────────────────────────────────────────────────────

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function gqlVars(range: ReportRange): { from: string; to: string; branchId?: string } {
  return { from: range.from, to: range.to, ...(range.branchId ? { branchId: range.branchId } : {}) }
}

function gqlCtx(signal?: AbortSignal): { fetchOptions: { signal: AbortSignal } } | undefined {
  return signal ? { fetchOptions: { signal } } : undefined
}

// ── Stub seeds (PREVIEW_STUB demo only) ─────────────────────────────────────

function stubDaily(): DailySalesPoint[] {
  const base = [
    [6, 12, 248500],
    [5, 9, 187200],
    [4, 18, 412300],
    [3, 21, 503100],
    [2, 14, 298700],
    [1, 16, 351400],
    [0, 8, 162800],
  ] as const
  return base.map(([d, count, cents]) => ({
    date: isoDaysAgo(d),
    ordersCount: count,
    salesCents: cents,
    avgTicketCents: count > 0 ? Math.round(cents / count) : 0,
  }))
}

function stubTopProducts(): TopProductRow[] {
  // Pre-sorted by totalCents desc (the contract guarantees order).
  return [
    { productId: 'p_arrachera', name: 'Arrachera (250g)', quantity: 41, totalCents: 738000 },
    { productId: 'p_pastor', name: 'Taco al pastor', quantity: 184, totalCents: 552000 },
    { productId: 'p_cerveza', name: 'Cerveza', quantity: 122, totalCents: 549000 },
    { productId: 'p_guac', name: 'Guacamole', quantity: 67, totalCents: 469000 },
    { productId: 'p_quesadilla', name: 'Quesadilla', quantity: 73, totalCents: 365000 },
    { productId: 'p_refresco', name: 'Refresco', quantity: 98, totalCents: 343000 },
    { productId: 'p_flan', name: 'Flan napolitano', quantity: 29, totalCents: 145000 },
  ]
}

function stubTipsByEmployee(): TipsByEmployeeRow[] {
  return [
    { employeeId: 'u_maria', employeeName: 'María López', pooledCents: 84200, directCents: 21500, totalCents: 105700 },
    { employeeId: 'u_jose', employeeName: 'José Ramírez', pooledCents: 84200, directCents: 12800, totalCents: 97000 },
    { employeeId: 'u_ana', employeeName: 'Ana Torres', pooledCents: 56100, directCents: 9400, totalCents: 65500 },
    { employeeId: 'u_luis', employeeName: 'Luis Méndez', pooledCents: 28000, directCents: 4200, totalCents: 32200 },
  ]
}

async function previewStub(): Promise<boolean> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  return PREVIEW_STUB
}

// ── API (graph-backed) ───────────────────────────────────────────────────────

/** Ventas por día del periodo — reportSales del grafo (erp-reports). */
export async function getDailySales(range: ReportRange, signal?: AbortSignal): Promise<DailySalesPoint[]> {
  const res = await gqlClient.query(PosReportSalesQuery, gqlVars(range), gqlCtx(signal)).toPromise()
  if (res.error) {
    if (await previewStub()) return stubDaily()
    throw new Error(res.error.message)
  }
  const rows = res.data?.reportSales?.rows ?? []
  if (rows.length === 0 && (await previewStub())) return stubDaily()
  // El read-model es por (branch, businessDate); colapsamos por dia sumando sucursales.
  const byDay = new Map<string, { ordersCount: number; salesCents: number }>()
  for (const r of rows) {
    const date = r.businessDate.slice(0, 10)
    const cur = byDay.get(date) ?? { ordersCount: 0, salesCents: 0 }
    cur.ordersCount += r.ordersCount
    cur.salesCents += r.netCents
    byDay.set(date, cur)
  }
  return [...byDay.entries()]
    .map(([date, v]) => ({
      date,
      ordersCount: v.ordersCount,
      salesCents: v.salesCents,
      avgTicketCents: v.ordersCount > 0 ? Math.round(v.salesCents / v.ordersCount) : 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

/** Productos mas vendidos del periodo — reportTopProducts del grafo. */
export async function getTopProducts(range: ReportRange, limit = 25, signal?: AbortSignal): Promise<TopProductRow[]> {
  const res = await gqlClient
    .query(PosReportTopProductsQuery, { ...gqlVars(range), limit }, gqlCtx(signal))
    .toPromise()
  if (res.error) {
    if (await previewStub()) return stubTopProducts().slice(0, limit)
    throw new Error(res.error.message)
  }
  const rows = res.data?.reportTopProducts ?? []
  if (rows.length === 0 && (await previewStub())) return stubTopProducts().slice(0, limit)
  return rows
    .map((r) => ({ productId: r.productId, name: r.productName, quantity: r.qty, totalCents: r.grossCents }))
    .sort((a, b) => b.totalCents - a.totalCents)
    .slice(0, limit)
}

/** Propinas por empleado del periodo — reportTips (pool/directas por mesero) del grafo,
 *  enriquecido con el nombre del mesero desde reportTopServers. */
export async function getTipsByEmployee(range: ReportRange, signal?: AbortSignal): Promise<TipsByEmployeeRow[]> {
  const ctx = gqlCtx(signal)
  const vars = gqlVars(range)
  const [tipsRes, serversRes] = await Promise.all([
    gqlClient.query(PosReportTipsQuery, vars, ctx).toPromise(),
    gqlClient.query(PosReportTopServersQuery, { ...vars, limit: 200 }, ctx).toPromise(),
  ])
  if (tipsRes.error) {
    if (await previewStub()) return stubTipsByEmployee()
    throw new Error(tipsRes.error.message)
  }
  const tipRows = tipsRes.data?.reportTips?.rows ?? []
  if (tipRows.length === 0 && (await previewStub())) return stubTipsByEmployee()
  const names = new Map<string, string>()
  for (const s of serversRes.data?.reportTopServers ?? []) names.set(s.serverId, s.serverName)
  const byServer = new Map<string, TipsByEmployeeRow>()
  for (const r of tipRows) {
    const id = r.serverId ?? '__pool__'
    const cur =
      byServer.get(id) ??
      { employeeId: id, employeeName: names.get(id) ?? (r.serverId ? r.serverId.slice(0, 8) : 'Pool'), pooledCents: 0, directCents: 0, totalCents: 0 }
    cur.pooledCents += r.pooledCents
    // El read-model trae total + pooled + distributed; las directas = total menos pooled.
    cur.directCents += Math.max(0, r.totalCents - r.pooledCents)
    cur.totalCents += r.totalCents
    byServer.set(id, cur)
  }
  return [...byServer.values()].sort((a, b) => b.totalCents - a.totalCents)
}

/** Cortes de caja (X/Z) del periodo — reportCuts del grafo. */
export async function getCutsReport(range: ReportRange, limit = 50, signal?: AbortSignal): Promise<CashCutReportRow[]> {
  const res = await gqlClient.query(PosReportCutsQuery, { ...gqlVars(range), limit }, gqlCtx(signal)).toPromise()
  if (res.error) throw new Error(res.error.message)
  return (res.data?.reportCuts ?? []).map((r) => ({
    cashCutId: r.cashCutId,
    branchId: r.branchId,
    businessDate: r.businessDate,
    shiftId: r.shiftId ?? null,
    kind: r.kind,
    expectedCents: r.expectedCents,
    countedCents: r.countedCents,
    varianceCents: r.varianceCents,
    cutAt: r.cutAt,
  }))
}

// ── CSV export ─────────────────────────────────────────────────────────────

function csvEscape(v: string | number): string {
  const s = String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function rowsToCsv(headers: string[], rows: (string | number)[][]): string {
  const lines = [headers.map(csvEscape).join(',')]
  for (const r of rows) lines.push(r.map(csvEscape).join(','))
  return lines.join('\n')
}

/** Triggers a client-side CSV download. No-op under jsdom (no document/anchor). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export { isoDaysAgo as reportsIsoDaysAgo }
